import type { Pmf } from '../prob/discrete-rv';

export type RvPresetId = '2d6-sum' | '2d6-diff' | '2d6-max' | 'coin-flips-count' | 'custom-payoff';

export interface RvPreset {
  id: RvPresetId;
  labelKey: `submode.random-variables.${RvPresetId}.label`;
  pmf: Pmf;
}

function diceSumPmf(): Pmf {
  const counts = new Array<number>(13).fill(0);
  for (let a = 1; a <= 6; a++) for (let b = 1; b <= 6; b++) counts[a + b]!++;
  const result: { value: number; prob: number }[] = [];
  for (let v = 2; v <= 12; v++) result.push({ value: v, prob: counts[v]! / 36 });
  return result;
}

function diceDiffPmf(): Pmf {
  const counts = new Array<number>(6).fill(0);
  for (let a = 1; a <= 6; a++) for (let b = 1; b <= 6; b++) counts[Math.abs(a - b)]!++;
  return counts.map((c, v) => ({ value: v, prob: c / 36 }));
}

function diceMaxPmf(): Pmf {
  const counts = new Array<number>(7).fill(0);
  for (let a = 1; a <= 6; a++) for (let b = 1; b <= 6; b++) counts[Math.max(a, b)]!++;
  return counts.slice(1).map((c, v) => ({ value: v + 1, prob: c / 36 }));
}

function coinHeadsPmf(n: number): Pmf {
  // Binomial(n, 0.5).
  const out: { value: number; prob: number }[] = [];
  let c = 1;
  for (let k = 0; k <= n; k++) {
    out.push({ value: k, prob: c / 2 ** n });
    c = (c * (n - k)) / (k + 1);
  }
  return out;
}

export const RV_PRESETS: readonly RvPreset[] = [
  { id: '2d6-sum', labelKey: 'submode.random-variables.2d6-sum.label', pmf: diceSumPmf() },
  { id: '2d6-diff', labelKey: 'submode.random-variables.2d6-diff.label', pmf: diceDiffPmf() },
  { id: '2d6-max', labelKey: 'submode.random-variables.2d6-max.label', pmf: diceMaxPmf() },
  {
    id: 'coin-flips-count',
    labelKey: 'submode.random-variables.coin-flips-count.label',
    pmf: coinHeadsPmf(5),
  },
  {
    id: 'custom-payoff',
    labelKey: 'submode.random-variables.custom-payoff.label',
    pmf: [
      { value: 0, prob: 0.5 },
      { value: 1, prob: 0.5 },
    ],
  },
];

export function rvSample(preset: RvPreset, u: number): number {
  let acc = 0;
  for (const e of preset.pmf) {
    acc += e.prob;
    if (u < acc) return e.value;
  }
  return preset.pmf[preset.pmf.length - 1]!.value;
}
